import { LinksFunction, LoaderFunctionArgs, json } from "@remix-run/node";
import type { ShouldRevalidateFunctionArgs } from "@remix-run/react";
import {
	Links,
	Meta,
	Outlet,
	Scripts,
	ScrollRestoration,
	useLoaderData,
	useLocation,
	useSubmit,
	useRouteError
} from "@remix-run/react";
import stylesheet from "~/tailwind.css?url";
import { Amplify } from "aws-amplify";
import amplifyConfig from "./amplifyconfiguration.json";
import { Toaster } from "./components/ui/sonner";
Amplify.configure(amplifyConfig, { ssr: true });
import { getToast } from "remix-toast";
import { toast as setToast } from "sonner";
import { useEffect } from "react";
import {
	buildAuthFormData,
	expireClientSession,
	getClientAuthSession
} from "./lib/auth-client";
import {
	AUTH_EXPIRED_EVENT,
	REFRESH_SESSION_PATH,
	isProtectedPath
} from "./lib/auth-utils";
import { PHProvider } from "./provider";
import { Error9 } from "./components/error_page";
import { extractErrorInfo } from "./lib/error-utils";

export const links: LinksFunction = () => [
	{ rel: "stylesheet", href: stylesheet },
	{ rel: "stylesheet", href: "https://rsms.me/inter/inter.css" }
];

export async function loader({ request }: LoaderFunctionArgs) {
	const { toast, headers } = await getToast(request);
	return json({ toast }, { headers });
}

export function shouldRevalidate({ formAction }: ShouldRevalidateFunctionArgs) {
	return formAction != null;
}

export const ErrorBoundary = () => {
	const error = useRouteError();
	const info = extractErrorInfo(error);

	return (
		<html lang="en">
			<head>
				<meta charSet="utf-8" />
				<meta
					name="viewport"
					content="width=device-width, initial-scale=1"
				/>
				<link
					rel="stylesheet"
					href={stylesheet}
				/>
				<link
					rel="stylesheet"
					href="https://rsms.me/inter/inter.css"
				/>
				<title>{`${info.errorCode} | ${info.title}`}</title>
			</head>
			<body className="min-h-screen bg-background text-foreground">
				<Error9
					errorCode={info.errorCode}
					title={info.title}
					description={info.description}
					details={{
						...info.details,
						rawMessage: info.rawMessage
					}}
					stack={info.stack}
					raw={info.raw}
					secondaryAction={{
						label: "Contact us",
						to: "/contact",
						variant: "outline"
					}}
					primaryAction={{
						label: "Go Home",
						to: "/"
					}}
				/>
				<Scripts />
			</body>
		</html>
	);
};

export default function App() {
	const { toast } = useLoaderData<typeof loader>();
	const submit = useSubmit();
	const { pathname } = useLocation();

	useEffect(() => {
		if (toast) {
			setToast(toast.message);
		}
	}, [toast]);

	useEffect(() => {
		if (!isProtectedPath(pathname)) return;

		let cancelled = false;

		async function refreshSession() {
			const existing = await getClientAuthSession();
			if (cancelled) return;
			if (existing.status === "expired") {
				await expireClientSession();
				return;
			}
			if (existing.status !== "ok") {
				return;
			}
			submit(buildAuthFormData(existing.session, existing.token), {
				method: "POST",
				action: REFRESH_SESSION_PATH,
				navigate: false
			});
		}

		void refreshSession();
		const intervalId = window.setInterval(refreshSession, 45 * 60 * 1000);

		function onAuthExpired() {
			void expireClientSession();
		}
		window.addEventListener(AUTH_EXPIRED_EVENT, onAuthExpired);

		return () => {
			cancelled = true;
			window.clearInterval(intervalId);
			window.removeEventListener(AUTH_EXPIRED_EVENT, onAuthExpired);
		};
	}, [pathname, submit]);

	return (
		<html lang="en">
			<head>
				<meta charSet="utf-8" />
				<meta
					name="viewport"
					content="width=device-width, initial-scale=1"
				/>
				<Meta />
				<Links />
			</head>
			<PHProvider>
				<body className={`w-screen h-screen overflow-y-hidden`}>
					<ScrollRestoration />
					<Scripts />
					<Outlet />
					<Toaster />
				</body>
			</PHProvider>
		</html>
	);
}
